import { XIcon } from '@phosphor-icons/react'
import { Toast as T } from 'radix-ui'
import { useSyncExternalStore, type ReactNode } from 'react'
import { Button, IconButton } from './Button.tsx'

export interface ToastInput {
  title: ReactNode
  description?: ReactNode
  tone?: 'neutral' | 'live' | 'warn' | 'fault'
  /** e.g. Undo. Runs, then dismisses the toast. */
  action?: { label: string; onClick: () => void; altText?: string }
  duration?: number
}

interface ToastItem extends ToastInput {
  id: number
  open: boolean
}

let items: ToastItem[] = []
let seq = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

/** Shows a toast. Returns a function that dismisses it. */
export function toast(input: ToastInput): () => void {
  const id = ++seq
  items = [...items.slice(-3), { ...input, id, open: true }]
  emit()
  return () => dismiss(id)
}

function dismiss(id: number) {
  items = items.map((t) => (t.id === id ? { ...t, open: false } : t))
  emit()
  window.setTimeout(() => {
    items = items.filter((t) => t.id !== id)
    emit()
  }, 250)
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** Mount once (UIProvider does). */
export function Toaster() {
  const list = useSyncExternalStore(subscribe, () => items, () => items)
  return (
    <T.Provider swipeDirection="right" duration={5000}>
      {list.map((t) => (
        <T.Root
          key={t.id}
          className="rt-toast"
          data-tone={t.tone ?? 'neutral'}
          open={t.open}
          duration={t.duration ?? (t.action ? 8000 : 5000)}
          onOpenChange={(o) => {
            if (!o) dismiss(t.id)
          }}
        >
          <T.Title className="rt-toast__title">{t.title}</T.Title>
          {t.description ? <T.Description className="rt-toast__desc">{t.description}</T.Description> : null}
          <div className="rt-toast__actions">
            {t.action ? (
              <T.Action altText={t.action.altText ?? t.action.label} asChild>
                <Button size="sm" variant="secondary" onClick={t.action.onClick}>
                  {t.action.label}
                </Button>
              </T.Action>
            ) : null}
            <T.Close asChild>
              <IconButton label="Dismiss" icon={<XIcon />} size="sm" tooltip={false} />
            </T.Close>
          </div>
        </T.Root>
      ))}
      <T.Viewport className="rt-toast-viewport" />
    </T.Provider>
  )
}
